import type { FormKitProps } from '@formkit/core'
import { nonNullable } from '#vuepal/helpers/type'
import type {
  WebformElementMapper,
  WebformMapperContextGlobal,
} from './defineWebformElementMapper'
import {
  type WebformElementStatesFragment,
  WebformStateConditionTrigger,
  WebformStateLogic,
} from '#graphql-operations'
import type { NormalizedWebformItem } from './asWebformItem'

const TRIGGER_OPERATOR_MAP: Partial<
  Record<WebformStateConditionTrigger, string>
> = {
  [WebformStateConditionTrigger.VALUE]: '===',
}

const LOGIC_OPERATOR_MAP: Partial<Record<WebformStateLogic, string>> = {
  [WebformStateLogic.AND]: '&&',
  [WebformStateLogic.OR]: '||',
  [WebformStateLogic.XOR]: '||',
}

function escapeConditionValue(value: unknown): string {
  return String(value).replace(/"/g, '\\"')
}

/**
 * Map the WebformElementStates to FormKit conditions.
 *
 * CHECKED/UNCHECKED use $isChecked (a helper passed via FormKitSchema :data)
 * which handles both single checkboxes (boolean) and checkbox groups (array).
 */
function mapCondition(
  property: keyof WebformElementStatesFragment,
  states?: WebformElementStatesFragment,
): Partial<FormKitProps> {
  if (!property || !states?.[property]?.logic) {
    return {}
  }

  const state = states[property]
  const operator = LOGIC_OPERATOR_MAP[state.logic!]
  if (!operator) {
    return {}
  }

  const conditions = (state.conditions || [])
    .map((condition) => {
      const { trigger, field, fieldValue } = condition ?? {}

      if (trigger === WebformStateConditionTrigger.FILLED) {
        return `$get(${field}).value`
      }

      if (trigger === WebformStateConditionTrigger.CHECKED) {
        return `$isChecked($get(${field}).value, "${escapeConditionValue(fieldValue)}")`
      }

      if (trigger === WebformStateConditionTrigger.UNCHECKED) {
        return `$isChecked($get(${field}).value, "${escapeConditionValue(fieldValue)}") === false`
      }

      const op = trigger && TRIGGER_OPERATOR_MAP[trigger]
      if (field && op) {
        return `$get(${field}).value ${op} "${escapeConditionValue(fieldValue)}"`
      }

      return null
    })
    .filter(nonNullable)
    .join(` ${operator} `)

  if (!conditions) {
    return {}
  }

  console.log(conditions)

  return { if: conditions }
}

function mapSharedProps(item: NormalizedWebformItem): Partial<FormKitProps> {
  const {
    name,
    required,
    title,
    help,
    element,
    states,
    helpTitle,
    fieldPrefix,
    fieldSuffix,
  } = item

  const label = title
  const validation = []

  if (required || states?.required) {
    validation.push(['required'])
  }

  let helpMarkup = ''

  if (helpTitle) {
    helpMarkup += `<p><strong>${helpTitle}</strong></p>`
  }

  if (help) {
    helpMarkup += `<div>${help}</div>`
  }

  const id = name.replace('[0]', '')

  const props: Partial<FormKitProps> = {
    name: id,
    key: id,
    id,
    label,
    validation,
    help,
    __raw__sectionsSchema: {
      help: {
        children: '',
        attrs: {
          innerHTML: helpMarkup,
        },
      },
      prefix: {
        children: fieldPrefix,
      },
      suffix: {
        children: fieldSuffix,
      },
    },
    ...mapCondition('visible', item.states ?? undefined),
    readonly: element && 'readonly' in element ? !!element.readonly : false,
  }

  return props
}

export const getSchemaBuilder = async (global: WebformMapperContextGlobal) => {
  const imports = import.meta.glob('./mappers/*.ts')
  /* eslint-disable  @typescript-eslint/no-explicit-any */
  const mappers: WebformElementMapper<any>[] = await Promise.all(
    Object.values(imports).map((v) => {
      /* eslint-disable  @typescript-eslint/no-explicit-any */
      return v().then((module: any) => {
        return module.default
      })
    }),
  )

  // Create the map of GraphQL element type => mapper.
  /* eslint-disable  @typescript-eslint/no-explicit-any */
  const mapperTypeMap = mappers.reduce<
    Record<string, WebformElementMapper<any>>
  >((acc, mapper) => {
    mapper.types.forEach((type) => {
      acc[type] = mapper
    })

    return acc
  }, {})

  const buildChildren = (items: NormalizedWebformItem[]) => {
    return items
      .map((item) => {
        const typename = item.element?.__typename
        if (!typename) {
          return null
        }
        const mapper = mapperTypeMap[typename]
        if (!mapper) {
          return null
        }

        /* eslint-disable  @typescript-eslint/no-explicit-any */
        const mapped = mapper.build(item as any, ctx as any)
        if (!mapped) {
          return null
        }

        // Props shared for every form item.
        const sharedProps = mapSharedProps(item)
        const sharedValidation = sharedProps.validation || []
        const mappedValidation =
          mapped && 'validation' in mapped ? mapped.validation || [] : []
        return {
          ...sharedProps,
          ...mapped,
          validation: [...sharedValidation, ...mappedValidation],
        }
      })
      .filter(nonNullable)
  }

  const ctx = {
    buildChildren,
    global,
  }

  return { buildChildren }
}
